import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';

import TuneRoundedIcon from '@mui/icons-material/TuneRounded';

import { MOTION, iconColor } from './chatTheme';

/**
 * Полоска папок над списком чатов.
 *
 * Прокручивается по горизонтали, активная вкладка подчёркнута общей
 * «живой» чертой: она переезжает между вкладками через layoutId, а не
 * перерисовывается — от этого переключение читается как движение, а не
 * как мигание.
 *
 * У каждой папки свой счётчик — число ЧАТОВ с непрочитанным, а не сумма
 * сообщений: в полоске важно «где есть что почитать», а не сколько именно.
 */
export default function FolderStrip({
  folders = [],
  activeId = null,          // null — вкладка «Все»
  allUnread = 0,
  onSelect,
  onManage,
}) {
  const scrollerRef = useRef(null);
  const activeRef = useRef(null);

  useEffect(() => {
    const node = scrollerRef.current;
    if (!node) return undefined;
    const onWheel = event => {
      // Preserve native horizontal trackpad scrolling and browser zoom.
      if (event.ctrlKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
      const factor = event.deltaMode === 1 ? 24 : event.deltaMode === 2 ? node.clientWidth : 1;
      const delta = event.deltaY * factor;
      const limit = Math.max(0, node.scrollWidth - node.clientWidth);
      if (!limit || (delta < 0 && node.scrollLeft <= 0) || (delta > 0 && node.scrollLeft >= limit - 1)) return;
      event.preventDefault();
      node.scrollLeft = Math.min(limit, Math.max(0, node.scrollLeft + delta));
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [folders.length]);

  // Активная вкладка всегда должна быть на виду — например, после выбора
  // папки из дальнего конца списка или после перезагрузки страницы.
  useEffect(() => {
    const node = activeRef.current;
    if (node?.scrollIntoView) {
      node.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  }, [activeId, folders.length]);

  if (folders.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'stretch',
        borderBottom: '1px solid', borderColor: 'divider',
        minWidth: 0,
      }}
    >
      <Box
        ref={scrollerRef}
        sx={{
          display: 'flex', alignItems: 'center',
          flex: 1, minWidth: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          px: 0.75,
          // Полоску листают пальцем и колесом; сама полоса прокрутки тут
          // только мешает — она съедает и без того небольшую высоту.
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Tab id={null} label="Все" emoji="" count={allUnread}
             activeId={activeId} activeRef={activeRef} onSelect={onSelect} />
        {folders.map((f) => (
          <Tab key={f.id} id={f.id} label={f.name} emoji={f.emoji} count={f.unread_count}
               activeId={activeId} activeRef={activeRef} onSelect={onSelect} />
        ))}
      </Box>

      <Tooltip title="Настроить папки" size="sm">
        <IconButton
          size="sm" variant="plain" color="neutral"
          onClick={onManage}
          sx={(theme) => ({
            flexShrink: 0, alignSelf: 'center', mr: 0.75,
            borderRadius: '50%',
            ...iconColor(theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.5)' : 'rgba(71,84,103,0.6)'),
            '&:hover': {
              backgroundColor: 'background.level1',
              ...iconColor('var(--joy-palette-primary-500)'),
            },
          })}
        >
          <TuneRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}


/**
 * Одна вкладка полоски.
 *
 * Объявлена НА УРОВНЕ МОДУЛЯ, а не внутри FolderStrip. Внутри она была
 * новым типом компонента на каждый рендер: React размонтировал все вкладки
 * и монтировал заново, а вместе с ними — и общую черту с layoutId. Framer
 * считал её новым узлом и заново «переезжал» на неё, обычно от первой
 * вкладки. Отсюда и брались повторные проигрывания, и подчёркивание,
 * едущее со «Все», хотя человек переключался с «Деловых».
 */
const Tab = ({ id, label, emoji, count, activeId, activeRef, onSelect }) => {
    const active = String(id ?? 'all') === String(activeId ?? 'all');
    return (
      <Box
        ref={active ? activeRef : undefined}
        component={motion.button}
        type="button"
        whileTap={{ scale: 0.95 }}
        onClick={() => onSelect(id)}
        sx={(theme) => ({
          position: 'relative',
          flexShrink: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          px: 1.25, pt: 0.75, pb: 1,
          display: 'flex', alignItems: 'center', gap: 0.625,
          fontSize: 13,
          fontWeight: active ? 750 : 600,
          whiteSpace: 'nowrap',
          color: active
            ? 'var(--joy-palette-primary-500)'
            : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.62)' : 'rgba(71,84,103,0.78)'),
          transition: 'color .18s',
          '&:hover': { color: 'var(--joy-palette-primary-400)' },
        })}
      >
        {emoji ? <Box component="span" sx={{ fontSize: 14 }}>{emoji}</Box> : null}
        {label}
        {count > 0 && (
          <Box
            component="span"
            sx={{
              minWidth: 17, height: 17, px: 0.5,
              borderRadius: '9px',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10.5, fontWeight: 800, lineHeight: 1,
              backgroundColor: active ? 'primary.500' : 'rgba(99,102,241,0.16)',
              color: active ? '#fff' : 'primary.500',
            }}
          >
            {count > 99 ? '99+' : count}
          </Box>
        )}
        {active && (
          // Одна общая черта на всю полоску: framer переносит её между
          // вкладками, поэтому подчёркивание «едет», а не перескакивает.
          <Box
            component={motion.div}
            layoutId="folder-underline"
            transition={MOTION.spring}
            sx={{
              position: 'absolute', left: 8, right: 8, bottom: 2,
              height: '2.5px', borderRadius: '2px',
              backgroundColor: 'primary.500',
            }}
          />
        )}
      </Box>
    );
};
